import type { ApplicationModule, ApplicationModuleCode } from '@/domain/application-modules/application-module.interface';
import { allTrue } from '@/utils/rxjs/operators/all-true.operator';
import { waitUntil } from '@/utils/rxjs/operators/wait-until.operator';
import { isNullish } from '@/utils/typing/is-nullish.predicate';
import { inject, Injectable, signal } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { combineLatest, firstValueFrom, type Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import type { ProjectId } from '../../interfaces/Project.interface';
import { ProfileProvider } from '../../providers/profile/profile.provider';
import type { AppState } from '../../store/app.state';
import { ApplicationModulesStoreActions, ApplicationModulesStoreSelectors } from '../../store/entities/application-modules';
import { AdvizeoCoreApplicationModulesProvider } from './advizeo-core-application-modules.provider';
import type { ApplicationModulesService } from './application-modules.service';

@Injectable({
    providedIn: 'root'
})
export class ApplicationModulesServiceAdapter implements ApplicationModulesService {
    private _advizeoCoreApplicationModulesProvider = inject(AdvizeoCoreApplicationModulesProvider);
    private _profileProvider = inject(ProfileProvider);
    private _store = inject<Store<AppState>>(Store);

    private _applicationModules = signal<ApplicationModule[] | null>(null);

    fetchApplicationModules(projectId: ProjectId): Promise<ApplicationModule[]> {
        this._store.dispatch(new ApplicationModulesStoreActions.FetchApplicationModules());
        return firstValueFrom(this._advizeoCoreApplicationModulesProvider.fetchApplicationModules$(projectId))
            .then(applicationModules => {
                this._applicationModules.set(applicationModules);
                this._store.dispatch(new ApplicationModulesStoreActions.FetchApplicationModulesSucceeded({ applicationModules }));
                return applicationModules;
            })
            .catch(error => {
                this._store.dispatch(new ApplicationModulesStoreActions.FetchApplicationModulesFailed());
                throw error;
            });
    }

    getApplicationModules$(): Observable<ApplicationModule[]> {
        return this._store.pipe(select(ApplicationModulesStoreSelectors.selectApplicationModules));
    }

    getApplicationModuleByCode$(applicationModuleCode: ApplicationModuleCode): Observable<ApplicationModule | null> {
        return this._store.pipe(select(ApplicationModulesStoreSelectors.selectApplicationModuleByCode(applicationModuleCode)));
    }

    getApplicationModuleByCodeR(applicationModuleCode: ApplicationModuleCode): ApplicationModule | undefined {
        return this._applicationModules()?.find(applicationModule => applicationModule.code === applicationModuleCode);
    }

    hasModuleAccessR(code: ApplicationModuleCode): boolean {
        let applicationModule = this.getApplicationModuleByCodeR(code);
        if (isNullish(applicationModule)) {
            return false;
        }
        if (applicationModule.isOpenToPublic) {
            return true;
        }
        if (applicationModule.isOpenToInternal && this._profileProvider.hasInternalModuleAccessR()) {
            return true;
        }
        return false;
    }

    hasModuleAccess(code: ApplicationModuleCode): Promise<boolean> {
        return firstValueFrom(
            this.hasModuleAccess$(code).pipe(
                waitUntil(
                    allTrue([
                        this._store.pipe(
                            select(ApplicationModulesStoreSelectors.selectIsLoaded),
                            filter(isLoaded => isLoaded)
                        ),
                        this._profileProvider.areRightsLoaded$().pipe(filter(isLoaded => isLoaded))
                    ])
                )
            )
        );
    }

    hasModuleAccess$(code: ApplicationModuleCode): Observable<boolean> {
        return combineLatest([this.getApplicationModuleByCode$(code), this._profileProvider.hasInternalModuleAccess$()]).pipe(
            map(([applicationModule, hasInternalModuleAccess]) => {
                if (isNullish(applicationModule)) {
                    return false;
                }
                if (applicationModule.isOpenToPublic) {
                    return true;
                }
                if (applicationModule.isOpenToInternal && hasInternalModuleAccess) {
                    return true;
                }
                return false;
            })
        );
    }

    hasEveryoneAccessToModuleInCurrentProject$(code: ApplicationModuleCode): Observable<boolean> {
        return this.getApplicationModuleByCode$(code).pipe(
            map(applicationModule => {
                if (isNullish(applicationModule)) {
                    return false;
                }
                if (applicationModule.isOpenToPublic) {
                    return true;
                }
                return false;
            })
        );
    }
}
